import * as React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import Alert, { type AlertSeverity } from 'src/shared/components/ui/alert';

export type SnackbarAlertProps = {
    message: React.ReactNode;
    severity?: AlertSeverity;
    autoHideDuration?: number;
    open: boolean;
    onClose?: () => void;
    dataTestId?: string;
};

const severityIcons: Record<AlertSeverity, React.ReactNode> = {
    success: <CheckCircle2 className='size-[18px] shrink-0' aria-hidden />,
    error: <AlertCircle className='size-[18px] shrink-0' aria-hidden />,
    warning: <AlertTriangle className='size-[18px] shrink-0' aria-hidden />,
    info: <Info className='size-[18px] shrink-0' aria-hidden />,
};

const SnackbarAlert = (props: SnackbarAlertProps) => {
    const { autoHideDuration, message, severity = 'success', open, onClose, dataTestId } = props;
    const [isMounted, setIsMounted] = React.useState(false);

    React.useEffect(() => {
        setIsMounted(true);
    }, []);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        if (!autoHideDuration) {
            return;
        }
        if (!onClose) {
            return;
        }
        const timerId = window.setTimeout(() => {
            onClose();
        }, autoHideDuration);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [open, autoHideDuration, onClose]);

    if (!open) {
        return null;
    }

    if (!isMounted) {
        return null;
    }

    const content = (
        <div className='pointer-events-none fixed inset-x-0 bottom-4 z-[1400] flex justify-center px-4' data-testid={dataTestId}>
            <div className='pointer-events-auto w-full max-w-lg'>
                <Alert severity={severity} icon={severityIcons[severity]} className='shadow-lg ring-1 ring-black/5'>
                    <div className='flex w-full min-w-0 items-start gap-2'>
                        <div className='min-w-0 flex-1 text-base font-medium leading-snug'>{message}</div>
                        {onClose ? (
                            <button
                                type='button'
                                aria-label='close'
                                onClick={onClose}
                                className='-mr-0.5 -mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-current/70 transition-colors hover:bg-black/5 hover:text-current focus:outline-none focus-visible:ring-2 focus-visible:ring-current/30'
                            >
                                <X className='size-4' aria-hidden />
                            </button>
                        ) : null}
                    </div>
                </Alert>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

export default SnackbarAlert;
